import { vec3 } from 'gl-matrix';
import React, { useCallback, useEffect, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
    CircleRegion,
    CircleRegionDraft,
    circleRegionInFrame,
    MIN_CIRCLE_RADIUS_MM,
} from '~/domain/intensity/circleRegion';
import {
    euclideanDistanceMm,
    PointMm,
    toPointMm,
} from '~/domain/geometry/patientGeometry';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    CanvasPoint,
    patientToCanvasPoint,
    viewPlaneCoordinateMm,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import { circleIntensityStatistics } from '~/engine/data/image/circleIntensityStatistics';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useAnnotationPointDrag } from '~/hooks/viewer/annotationOverlay';
import { updateAnnotation } from '~/state/atoms/annotationListAtoms';
import {
    intensityPlacementActiveState,
    circleRegionDraftState,
    circleRegionListState,
    highlightedCircleRegionState,
} from '~/state/atoms/intensityToolAtoms';
import { formatDistanceCm } from '~/utils/measurementFormat';
import { OFF_PLANE_OPACITY } from '../AnnotationOverlaySvg';
import AnnotationPlaneOverlay, {
    AnnotationAppearance,
    OVERLAY_LABEL_STYLE,
} from '../AnnotationPlaneOverlay';

/** Committed circle colour (green — distinct from the line profile's blue). */
const CIRCLE_COLOR = '#66BB6A';
/** In-progress placement colour. */
const DRAFT_COLOR = '#4FC3F7';
const CENTER_HANDLE_PX = 3.5;
/**
 * While the circle being placed is smaller than this, its diameter label is
 * not drawn: it would sit on the centre dot and flicker during the first
 * pixels of the drag.
 */
const DRAFT_LABEL_MIN_RADIUS_PX = 12;
const EDGE_HANDLE_PX = 5;

interface CircleRegionOverlayProps {
    view: PlaneView | null;
}

/** Which part of a circle a drag moves. */
type CircleHandle = 'center' | 'edge';

interface CircleDrag {
    readonly id: string;
    readonly handle: CircleHandle;
    /**
     * The circle's centre. A resize measures the new radius from it, and a
     * move stays in the plane through it.
     */
    readonly centerMm: PointMm;
}

/** Diameter display string, e.g. `"⌀ 3.20 cm"`. */
function diameterLabel(radiusMm: number): string {
    return `⌀ ${formatDistanceCm(2 * radiusMm)}`;
}

/** The centre alone decides which slice a circle lies on. */
function circlePoints(circle: CircleRegion): readonly PointMm[] {
    return [circle.centerMm];
}

/**
 * On-screen radius, in px: a patient-space point one radius along the view's X
 * axis, projected — so it tracks zoom with no pixels-per-mm assumption.
 */
function projectedRadiusPx(
    centerMm: PointMm,
    radiusMm: number,
    center: CanvasPoint,
    view: PlaneView
): number {
    const edgeMm = toPointMm(
        vec3.scaleAndAdd(vec3.create(), centerMm, view.viewXDirection, radiusMm)
    );
    const edge = patientToCanvasPoint(edgeMm, view);
    return Math.hypot(edge.x - center.x, edge.y - center.y);
}

/** The latest cursor position of a drag, waiting for its animation frame. */
interface PendingCircleMove {
    readonly drag: CircleDrag;
    readonly pointMm: PointMm;
    readonly view: PlaneView;
}

/**
 * Dragging a placed circle by its centre (move) or its edge (resize). The
 * dragged point is kept in the circle's own plane, so an edit cannot move it
 * to another slice. The figures are read from the image the pane shows, in
 * the pane's frame, and the circle is stored in that image's own frame.
 *
 * Mouse moves arrive faster than frames are drawn, and every update re-reads
 * the voxels inside the circle, so the moves are applied once per animation
 * frame. The shape and its figures are updated in the same step, so the
 * panel never shows figures for a circle other than the one on screen.
 */
export function useCircleDrag(view: PlaneView | null): {
    beginDrag: (event: React.MouseEvent, drag: CircleDrag) => void;
    draggingId: string | null;
} {
    const pendingMove = useRef<PendingCircleMove | null>(null);
    const scheduledFrame = useRef<number | null>(null);

    const centerPlaneCoordinateMm = useCallback(
        (drag: CircleDrag, dragView: PlaneView) =>
            viewPlaneCoordinateMm(drag.centerMm, dragView),
        []
    );

    const applyPendingMove = useRecoilCallback(
        (callback) => () => {
            scheduledFrame.current = null;
            const move = pendingMove.current;
            pendingMove.current = null;
            if (!move) {
                return;
            }
            const { drag, pointMm, view: dragView } = move;
            const pane = paneImageOf(dragView);
            // A resize that would collapse the circle is refused, the
            // same rule placement applies; a move keeps the radius, so
            // it cannot collapse.
            const resizedMm = euclideanDistanceMm(drag.centerMm, pointMm);
            if (drag.handle === 'edge' && resizedMm < MIN_CIRCLE_RADIUS_MM) {
                return;
            }
            updateAnnotation(
                callback,
                circleRegionListState,
                drag.id,
                (circle) => {
                    // The view can have changed image since the move was
                    // queued, to another series or to this one re-imported
                    // and filling again; the edit is refused as placement
                    // refuses it.
                    if (
                        pane.kind === 'none' ||
                        !viewShowsPlane(dragView, circle.plane) ||
                        !pane.image.parent.isComplete
                    ) {
                        return circle;
                    }
                    const planeOnPane = pane.planeFromImageFrame(circle.plane);
                    const centerOnPaneMm =
                        drag.handle === 'center'
                            ? pointMm
                            : pane.fromImageFrame(circle.centerMm);
                    const radiusMm =
                        drag.handle === 'center' ? circle.radiusMm : resizedMm;
                    return {
                        ...circle,
                        centerMm:
                            drag.handle === 'center'
                                ? pane.toImageFrame(pointMm)
                                : circle.centerMm,
                        radiusMm,
                        stats: circleIntensityStatistics(
                            pane.image,
                            centerOnPaneMm,
                            radiusMm,
                            planeOnPane.viewDirection
                        ),
                    };
                }
            );
        },
        []
    );

    const movePoint = useCallback(
        (drag: CircleDrag, pointMm: PointMm, dragView: PlaneView) => {
            pendingMove.current = { drag, pointMm, view: dragView };
            if (scheduledFrame.current === null) {
                scheduledFrame.current =
                    requestAnimationFrame(applyPendingMove);
            }
        },
        [applyPendingMove]
    );

    // A drag outliving the overlay has nothing left to update.
    useEffect(
        () => () => {
            if (scheduledFrame.current !== null) {
                cancelAnimationFrame(scheduledFrame.current);
            }
        },
        []
    );

    const { beginDrag, dragging } = useAnnotationPointDrag(
        view,
        centerPlaneCoordinateMm,
        movePoint
    );

    return { beginDrag, draggingId: dragging?.id ?? null };
}

/**
 * Draws the placed circles on the viewport, each with its diameter label and,
 * when editable, drag handles at the centre and the edge, plus the circle
 * being placed. Only the handles respond to the mouse. The intensity figures
 * are not drawn here; the panel shows them, with their unit.
 */
export default function CircleRegionOverlay({
    view,
}: CircleRegionOverlayProps) {
    const circles = useRecoilValue(circleRegionListState.items);
    const draft = useRecoilValue(circleRegionDraftState);
    const placementActive = useRecoilValue(intensityPlacementActiveState);
    const highlightedId = useRecoilValue(highlightedCircleRegionState);
    const { beginDrag, draggingId } = useCircleDrag(view);

    return (
        <AnnotationPlaneOverlay
            view={view}
            items={circles}
            draft={draft}
            placementActive={placementActive}
            tool="intensity"
            highlightedId={highlightedId}
            draggingId={draggingId}
            beginDrag={beginDrag}
            itemInFrame={circleRegionInFrame}
            draftInFrame={circleRegionInFrame}
            pointsOf={circlePoints}
            renderItem={renderCircle}
            renderDraft={renderDraft}
        />
    );
}

function renderCircle(
    view: PlaneView,
    circle: CircleRegion,
    appearance: AnnotationAppearance,
    beginDrag: (event: React.MouseEvent, drag: CircleDrag) => void
) {
    const { onCurrentSlice, editable, emphasized } = appearance;
    const center = patientToCanvasPoint(circle.centerMm, view);
    const radiusPx = projectedRadiusPx(
        circle.centerMm,
        circle.radiusMm,
        center,
        view
    );
    const dimmed = !onCurrentSlice;
    const opacity = emphasized || !dimmed ? 1 : OFF_PLANE_OPACITY;
    return (
        <g key={circle.id} opacity={opacity}>
            <circle
                cx={center.x}
                cy={center.y}
                r={radiusPx}
                fill="none"
                stroke={CIRCLE_COLOR}
                strokeWidth={emphasized ? 2.5 : 1.5}
                strokeDasharray={dimmed ? '4 3' : undefined}
            />
            <text
                x={center.x}
                y={center.y - radiusPx - 6}
                textAnchor="middle"
                fontSize={12}
                fill={CIRCLE_COLOR}
                style={OVERLAY_LABEL_STYLE}
            >
                {diameterLabel(circle.radiusMm)}
            </text>
            {editable && (
                <>
                    <circle
                        cx={center.x}
                        cy={center.y}
                        r={CENTER_HANDLE_PX}
                        fill={CIRCLE_COLOR}
                        style={{ pointerEvents: 'all', cursor: 'move' }}
                        onMouseDown={(event) =>
                            beginDrag(event, {
                                id: circle.id,
                                handle: 'center',
                                centerMm: circle.centerMm,
                            })
                        }
                    />
                    <circle
                        cx={center.x + radiusPx}
                        cy={center.y}
                        r={EDGE_HANDLE_PX}
                        fill="none"
                        stroke={CIRCLE_COLOR}
                        strokeWidth={2}
                        style={{ pointerEvents: 'all', cursor: 'ew-resize' }}
                        onMouseDown={(event) =>
                            beginDrag(event, {
                                id: circle.id,
                                handle: 'edge',
                                centerMm: circle.centerMm,
                            })
                        }
                    />
                </>
            )}
        </g>
    );
}

/** Draws the circle being placed: a dot at the centre, and the circle following the cursor. */
function renderDraft(view: PlaneView, draft: CircleRegionDraft) {
    const center = patientToCanvasPoint(draft.centerMm, view);
    const radiusPx = projectedRadiusPx(
        draft.centerMm,
        draft.radiusMm,
        center,
        view
    );
    return (
        <g key="draft">
            <circle
                cx={center.x}
                cy={center.y}
                r={radiusPx}
                fill="none"
                stroke={DRAFT_COLOR}
                strokeWidth={1.5}
                strokeDasharray="4 3"
            />
            <circle
                cx={center.x}
                cy={center.y}
                r={CENTER_HANDLE_PX}
                fill={DRAFT_COLOR}
            />
            {radiusPx > DRAFT_LABEL_MIN_RADIUS_PX && (
                <text
                    x={center.x}
                    y={center.y - radiusPx - 6}
                    textAnchor="middle"
                    fontSize={12}
                    fill={DRAFT_COLOR}
                    style={OVERLAY_LABEL_STYLE}
                >
                    {diameterLabel(draft.radiusMm)}
                </text>
            )}
        </g>
    );
}
